/**
 * Minimal RFC 4180-style CSV parser (quoted fields, commas, CRLF).
 */

function parseCsvRow(line) {
  const out = [];
  let cur = "";
  let i = 0;
  let inQuotes = false;
  while (i < line.length) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          cur += '"';
          i += 2;
          continue;
        }
        inQuotes = false;
        i += 1;
        continue;
      }
      cur += ch;
      i += 1;
      continue;
    }
    if (ch === '"') {
      inQuotes = true;
      i += 1;
      continue;
    }
    if (ch === ",") {
      out.push(cur);
      cur = "";
      i += 1;
      continue;
    }
    cur += ch;
    i += 1;
  }
  out.push(cur);
  return out.map((c) => c.trim());
}

function splitCsvRecords(text) {
  const rows = [];
  let cur = "";
  let inQuotes = false;
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (ch === '"') {
      inQuotes = !inQuotes;
      cur += ch;
      continue;
    }
    if (!inQuotes && (ch === "\n" || ch === "\r")) {
      if (ch === "\r" && text[i + 1] === "\n") i += 1;
      if (cur.length > 0 || rows.length > 0) rows.push(cur);
      cur = "";
      continue;
    }
    cur += ch;
  }
  if (cur.length > 0) rows.push(cur);
  return rows;
}

/**
 * @returns {{ headers: string[], rows: Record<string, string>[] }}
 */
export function parseCsv(text) {
  const raw = String(text || "").replace(/^\uFEFF/, "");
  const lines = splitCsvRecords(raw).filter((l) => l.trim() !== "");
  if (lines.length === 0) {
    return { headers: [], rows: [] };
  }
  const headers = parseCsvRow(lines[0]).map((h, idx) => h || `column_${idx + 1}`);
  const rows = [];
  for (let li = 1; li < lines.length; li += 1) {
    const cells = parseCsvRow(lines[li]);
    if (cells.every((c) => c === "")) continue;
    const row = {};
    for (let c = 0; c < headers.length; c += 1) {
      row[headers[c]] = cells[c] ?? "";
    }
    if (cells.length > headers.length) {
      row._csv_overflow = cells
        .slice(headers.length)
        .map((c) => c.trim())
        .filter(Boolean)
        .join("\u001f");
    }
    rows.push(row);
  }
  return { headers, rows };
}

/** Escape a single cell for CSV output. */
export function escapeCsvCell(value) {
  const s = value == null ? "" : String(value);
  if (/[",\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

/**
 * @param {string[]} headers
 * @param {Record<string, string | number | boolean | null | undefined>[]} rows
 */
export function serializeCsv(headers, rows) {
  const lines = [headers.map(escapeCsvCell).join(",")];
  for (const row of rows) {
    lines.push(headers.map((h) => escapeCsvCell(row[h] ?? "")).join(","));
  }
  return `${lines.join("\r\n")}\r\n`;
}
